'use client';

import Image from 'next/image';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import type { DemoKey } from '../_lib/solutions.content';
import { DEMOS, ProductDemo } from './product-demos/demo-registry';

/**
 * Editorial photo with a live product demo floating over it. On large
 * screens the demo overlays the photo's lower corner; below `lg` it sits
 * under the photo at a readable size instead of shrinking into it.
 */
export function AudienceVisual({
  image,
  imageAlt,
  demo,
  locale,
  priority = false,
  tall = true,
}: {
  image: string;
  imageAlt: string;
  demo: DemoKey;
  locale: MarketingLocale;
  priority?: boolean;
  /** Portrait crop on desktop (phone demos read best against a tall photo). */
  tall?: boolean;
}) {
  const isPhone = DEMOS[demo].kind === 'phone';

  // Photo and demo share one grid cell on large screens (demo overlaid in
  // the bottom corner) and stack below that, so a single demo instance is
  // positioned by CSS instead of mounting one copy per breakpoint.
  return (
    <div className="grid grid-cols-1">
      <div className="relative overflow-hidden rounded-[28px] bg-[#dcd7cf] [grid-area:1/1]">
        <div
          className={cn(
            'relative w-full aspect-[4/3]',
            tall && (isPhone ? 'lg:aspect-[4/5]' : 'lg:aspect-square'),
          )}
        >
          <Image
            src={image}
            alt={imageAlt}
            fill
            priority={priority}
            sizes="(max-width: 1024px) 100vw, 640px"
            className="object-cover"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent"
          />
        </div>
      </div>

      <div
        className={cn(
          'relative z-10 mx-auto mt-6 w-full [grid-area:2/1] lg:mt-0 lg:[grid-area:1/1] lg:self-end',
          isPhone
            ? 'max-w-[290px] lg:mx-6 lg:mb-6 lg:w-[250px] lg:justify-self-end'
            : 'max-w-[760px] lg:mb-6 lg:w-auto lg:justify-self-stretch lg:px-6',
        )}
      >
        <ProductDemo demo={demo} locale={locale} />
      </div>
    </div>
  );
}
